import type { World } from './world';

export type { Blend, Body, Kit, Milestone, Palette, World, WorldDress, WorldFx } from './world';
export { contrast, hexOf, hsl, luminance, redRatio, sceneSeed } from './world';

export type Look = World;
export type LookId = string;

export const WORLD_MODULES: Readonly<Record<string, World>> = Object.fromEntries(
  Object.entries(import.meta.glob<World>('./worlds/*/index.ts', { eager: true, import: 'default' })),
);

export const WORLDS: readonly World[] = Object.values(WORLD_MODULES).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
export const LOOK_IDS: readonly LookId[] = WORLDS.map((world) => world.id);
export const DEFAULT_LOOK: LookId = 'arraial';

export function slugOf(name: string): LookId {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');
}

const BY_ID = new Map(WORLDS.map((world) => [world.id, world]));
const LEGACY = new Map(WORLDS.filter((world) => world.legacy).map((world) => [world.seed, world]));

export function isLookId(value: string | null | undefined): value is LookId {
  return typeof value === 'string' && BY_ID.has(value);
}

export function lookOf(id: LookId): Look {
  const world = BY_ID.get(id) ?? LEGACY.get(id) ?? BY_ID.get(DEFAULT_LOOK);
  if (world === undefined) throw new Error(`no world ${id}`);
  return world;
}

export function lookFrom(search: string, fallback: LookId = DEFAULT_LOOK): LookId {
  const raw = new URLSearchParams(search).get('look')?.trim().toLowerCase();
  if (isLookId(raw)) return raw;
  return LEGACY.get(raw ?? '')?.id ?? fallback;
}
